import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ArticleDisplay from './ArticleDisplay';
import GuessInput from './GuessInput';
import LanguageSelect from './LanguageSelect';
import Stats from './Stats';
import VictoryScreen from './VictoryScreen';
import { Button, Chip, Loader, Logo, Panel, SettingsBar, juice, sfx } from '../ui';
import { normalizeString, wordExistsInText, extractWords, hasCloseMatchInText, saveProgress, loadProgress, cleanupOldProgress } from '../utils';
import './Game.css';

function Game({ discordSdk, auth }) {
  const [article, setArticle] = useState(null);
  const [guessedWords, setGuessedWords] = useState(new Set());
  const [guessCount, setGuessCount] = useState(0);
  const [gameWon, setGameWon] = useState(false);
  const [showVictoryScreen, setShowVictoryScreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState(null);
  const [nextArticleIn, setNextArticleIn] = useState(null);
  const [feedback, setFeedback] = useState(null); // 'correct', 'incorrect', 'close', 'duplicate'
  const feedbackTimerRef = useRef(null);

  // Cleanup old localStorage entries on mount
  useEffect(() => {
    cleanupOldProgress();
  }, []);

  // Load daily Wikipedia article
  const loadDailyArticle = useCallback(async (lang = language) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/wiki/daily?lang=${lang}`);
      if (!response.ok) throw new Error('Failed to fetch article');

      const data = await response.json();
      setArticle(data);
      setNextArticleIn(data.nextArticleIn);

      // Only reset game state when loading for the first time or changing language
      if (!article || article.language !== lang || article.id !== data.id) {
        // Try to load saved progress for this article
        const savedProgress = loadProgress(lang, data.id);

        if (savedProgress) {
          console.log('Loaded saved progress:', savedProgress);
          setGuessedWords(savedProgress.guessedWords);
          setGuessCount(savedProgress.guessCount);
          setGameWon(savedProgress.gameWon);
          // Don't show victory screen on reload, user wants to see the article
          setShowVictoryScreen(false);
        } else {
          setGuessedWords(new Set());
          setGuessCount(0);
          setGameWon(false);
          setShowVictoryScreen(false);
        }
      }

      setLoading(false);
      sfx.play('powerOn');
    } catch (err) {
      console.error('Error loading article:', err);
      setError('Failed to load article. Please try again.');
      setLoading(false);
    }
  }, [language, article]);

  // Save progress whenever guessedWords, guessCount, or gameWon changes
  useEffect(() => {
    if (article && guessedWords.size > 0) {
      saveProgress(language, guessedWords, guessCount, article.id, gameWon);
    }
  }, [guessedWords, guessCount, article, language, gameWon]);

  // Update countdown timer every second
  useEffect(() => {
    if (!nextArticleIn) return;

    const interval = setInterval(() => {
      setNextArticleIn(prev => {
        if (prev <= 1000) {
          // Time to load new article
          loadDailyArticle();
          return null;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [nextArticleIn, loadDailyArticle]);

  // Format countdown timer
  const formatCountdown = (ms) => {
    if (!ms) return '';
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // Title words drive the HUD progress meter. Deduplicated, because a title
  // that repeats a word ("War of the Worlds" style) would otherwise report a
  // total the player can never reach — one guess reveals every copy.
  const titleWords = useMemo(
    () => (article ? [...new Set(extractWords(article.title))] : []),
    [article]
  );

  const foundTitleWords = useMemo(
    () => titleWords.filter((word) => guessedWords.has(word)).length,
    [titleWords, guessedWords]
  );

  // Process a guess
  const handleGuess = useCallback((word) => {
    if (!word || gameWon || !article) return;

    // Normalize the guess (removes accents, lowercase, trim)
    const normalizedGuess = normalizeString(word);

    // Check for duplicate
    if (guessedWords.has(normalizedGuess)) {
      setFeedback('duplicate');
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = setTimeout(() => setFeedback(null), 600);
      return 'duplicate';
    }

    // Check if word exists as a WHOLE WORD in article or title
    // This prevents "loc" from matching "clock"
    const wordExistsInContent = wordExistsInText(normalizedGuess, article.content);
    const wordExistsInTitle = wordExistsInText(normalizedGuess, article.title);
    const wordExists = wordExistsInContent || wordExistsInTitle;

    // Not an exact hit, but close to a word not yet found (stem or typo match)
    // — e.g. "nation" for "national". Checked against the pre-update set so
    // the guess itself never counts as "close to itself".
    const isCloseGuess = !wordExists && (
      hasCloseMatchInText(normalizedGuess, article.title, guessedWords) ||
      hasCloseMatchInText(normalizedGuess, article.content, guessedWords)
    );

    // Add the word to guessed words
    const updatedGuessedWords = new Set([...guessedWords, normalizedGuess]);
    setGuessedWords(updatedGuessedWords);
    setGuessCount(prev => prev + 1);

    // Set feedback
    const result = wordExists ? 'correct' : isCloseGuess ? 'close' : 'incorrect';
    setFeedback(result);

    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = setTimeout(() => setFeedback(null), 600);

    // Check if all words in the title have been guessed. Uses the same
    // punctuation-stripping tokenization as wordExistsInText, so a comma or
    // hyphen attached to a title word (e.g. "diphtérique,") can't make the
    // title impossible to fully guess.
    const allTitleWords = extractWords(article.title);

    // Check if ALL title words are in the guessed words
    const allTitleWordsGuessed = allTitleWords.every(titleWord => updatedGuessedWords.has(titleWord));

    if (allTitleWordsGuessed) {
      setGameWon(true);
      setShowVictoryScreen(true);

      // Win celebration. Fired here rather than in VictoryScreen so it starts
      // on the same frame the last word lands, under the modal's entrance.
      juice.shake('heavy');
      juice.flash('rgba(255, 210, 63, 0.55)', 520);
      juice.rain({ count: 70 });
      sfx.play('win');

      // Send activity update to Discord
      if (discordSdk) {
        discordSdk.commands.setActivity({
          activity: {
            details: `Found: ${article.title}`,
            state: `${guessCount + 1} guesses`,
            type: 0,
          },
        }).catch(err => console.error('Failed to update activity:', err));
      }
    }

    return result;
  }, [article, guessedWords, guessCount, gameWon, discordSdk]);

  // Handle the one-time language pick on the landing screen
  const handleLanguageSelect = (newLang) => {
    setLanguage(newLang);
    loadDailyArticle(newLang);
  };

  // Handle closing victory screen (keeps gameWon true to show revealed article)
  const handleCloseVictory = () => {
    setShowVictoryScreen(false);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  // Debug: reset progress for BOTH languages and return to the language
  // picker, so both can be retested without relaunching the whole activity.
  // Dev-only (see the button rendered below) - Discord's Electron client
  // intercepts a lot of keyboard shortcuts before they reach the activity
  // iframe, so this isn't wired to a key combo, just a visible button that
  // import.meta.env.DEV strips out of the production build entirely.
  const handleDebugReset = () => {
    console.log('[Debug] Resetting game progress...');

    const keys = Object.keys(localStorage);
    const decrypediaKeys = keys.filter(key => key.startsWith('decrypedia_'));
    decrypediaKeys.forEach(key => localStorage.removeItem(key));

    console.log('[Debug] Cleared', decrypediaKeys.length, 'localStorage entries');

    setGuessedWords(new Set());
    setGuessCount(0);
    setGameWon(false);
    setShowVictoryScreen(false);
    setArticle(null);
    setNextArticleIn(null);
    setLanguage(null);
  };

  if (!language) {
    return <LanguageSelect onSelect={handleLanguageSelect} />;
  }

  if (loading) {
    return <Loader message="Decrypting" hint="Pulling today's article from Wikipedia…" />;
  }

  if (error) {
    return (
      <div className="game game--fault">
        <Panel variant="hot" label="Fault" className="game__fault-panel">
          <p className="game__fault-message">{error}</p>
        </Panel>
        <Button variant="primary" size="lg" onClick={() => loadDailyArticle()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="game">
      {showVictoryScreen && (
        <VictoryScreen
          title={article.title}
          guessCount={guessCount}
          auth={auth}
          nextArticleIn={nextArticleIn}
          onClose={handleCloseVictory}
        />
      )}

      <header className="game__header">
        <div className="game__brand">
          <Logo size="sm" />
          <Chip tone="cyan">{language.toUpperCase()}</Chip>
        </div>

        <div className="game__header-right">
          {nextArticleIn && (
            <Chip tone="red" icon="⏱" className="game__countdown">
              <span className="game__countdown-label">Next</span>
              <span className="game__countdown-time u-digits">{formatCountdown(nextArticleIn)}</span>
            </Chip>
          )}
          <SettingsBar />
        </div>
      </header>

      {import.meta.env.DEV && (
        <div className="game__dev">
          <Button variant="link" onClick={handleDebugReset} sound={null} hoverSound={null}>
            Reset progress (dev)
          </Button>
        </div>
      )}

      <Stats guessCount={guessCount} found={foundTitleWords} total={titleWords.length} />

      {!gameWon && <GuessInput onGuess={handleGuess} feedback={feedback} />}

      {gameWon && (
        <Panel variant="hot" className="game__won" glow>
          <span className="game__won-mark">✓</span>
          <span className="game__won-text">
            Decrypted — the full article is revealed below
          </span>
        </Panel>
      )}

      <ArticleDisplay
        article={article}
        guessedWords={guessedWords}
        gameWon={gameWon}
      />
    </div>
  );
}

export default Game;
